import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Send, CheckCircle, Loader2, Heart, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIP_OPTIONS = [
  { key: 'none', label: 'Sin propina', pct: 0 },
  { key: '10', label: '10%', pct: 0.10 },
  { key: '15', label: '15%', pct: 0.15 },
  { key: '20', label: '20%', pct: 0.20 },
  { key: 'custom', label: 'Otra cantidad', pct: null },
];

const ReviewPage = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [selectedTour, setSelectedTour] = useState('');
  const [tipOption, setTipOption] = useState('15');
  const [customTip, setCustomTip] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tipStatus, setTipStatus] = useState(null);
  const [tourOptions, setTourOptions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/tours`).then(res => {
      setTourOptions(res.data.map(t => ({
        id: t.id,
        label: t.name?.es || t.name || t.id,
        price: t.price || 0
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tip = params.get('tip');
    if (tip === 'success') {
      setTipStatus('success');
      setSubmitted(true);
    } else if (tip === 'cancel') {
      setTipStatus('cancel');
      setSubmitted(true);
    }
  }, []);

  const tourPrice = tourOptions.find(t => t.id === selectedTour)?.price || 0;

  const tipAmount = (() => {
    if (tipOption === 'none') return 0;
    if (tipOption === 'custom') return parseFloat(customTip) || 0;
    const opt = TIP_OPTIONS.find(o => o.key === tipOption);
    return opt ? Math.round(tourPrice * opt.pct * 100) / 100 : 0;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating || !name.trim()) {
      toast.error('Por favor, indica tu valoración y nombre');
      return;
    }
    if (!selectedTour) {
      toast.error('Por favor, selecciona el tour que realizaste');
      return;
    }

    setLoading(true);
    try {
      // Create review
      const res = await axios.post(`${API}/reviews/quick`, {
        name: name.trim(),
        rating,
        comment: comment.trim(),
        email: email.trim() || null,
        country: country.trim() || null,
        tour_id: selectedTour,
        tip_amount: tipAmount,
      });
      const reviewId = res.data.review_id;

      // If tip > 0, redirect to Stripe
      if (tipAmount >= 1) {
        const tipRes = await axios.post(`${API}/tips/checkout`, {
          amount: tipAmount,
          review_id: reviewId,
          name: name.trim(),
          message: null,
          origin_url: window.location.origin,
        });
        window.location.href = tipRes.data.url;
        return;
      }

      // No tip, show success
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Error al enviar la valoración. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Tip success screen
  if (tipStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#F5F3EF] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md w-full text-center"
          data-testid="tip-success-screen"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-rose-100 flex items-center justify-center"
          >
            <Heart className="h-14 w-14 text-rose-500 fill-rose-500" />
          </motion.div>
          <h1 className="font-display text-3xl font-bold text-[#1C1917] mb-4">
            ¡Gracias por tu generosidad!
          </h1>
          <p className="text-[#57534E] mb-8 text-lg">
            Carlos ha recibido tu propina y tu valoración. Tu apoyo significa mucho.
          </p>
          <Button
            onClick={() => (window.location.href = '/')}
            className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full px-8"
            data-testid="tip-success-home-btn"
          >
            Volver a la web
          </Button>
        </motion.div>
      </div>
    );
  }

  // Review submitted success (no tip or tip cancelled)
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#F5F3EF] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md w-full text-center"
          data-testid="review-success-screen"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center"
          >
            <CheckCircle className="h-14 w-14 text-green-600" />
          </motion.div>
          <h1 className="font-display text-3xl font-bold text-[#1C1917] mb-4">
            ¡Gracias por tu valoración!
          </h1>
          <p className="text-[#57534E] mb-2 text-lg">
            Tu opinión nos ayuda a mejorar la experiencia de los tours en Tuk-Tuk por Madrid.
          </p>
          {tipStatus === 'cancel' && (
            <p className="text-sm text-[#78716C] mt-4 mb-2">
              La propina fue cancelada. No se ha realizado ningún cobro.
            </p>
          )}
          <div className="border-t border-[#E5E5E5] pt-6 mt-6">
            <p className="text-sm text-[#78716C] mb-4">
              ¿Te gustaría compartir también en...?
            </p>
            <div className="flex justify-center gap-4 opacity-50">
              <div className="px-4 py-2 bg-[#FDFBF7] rounded-full text-sm text-[#57534E]">
                Google (próximamente)
              </div>
              <div className="px-4 py-2 bg-[#FDFBF7] rounded-full text-sm text-[#57534E]">
                TripAdvisor (próximamente)
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Button
              onClick={() => (window.location.href = '/')}
              className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full px-8"
              data-testid="review-success-home-btn"
            >
              Volver a la web
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#F5F3EF] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 max-w-lg w-full"
        data-testid="review-page"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C2410C] flex items-center justify-center">
            <span className="text-3xl">&#128666;</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1C1917] leading-tight">
            ¿Cómo ha sido tu tour en Tuk-Tuk por Madrid con Carlos Casal?
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tour Selection */}
          <div data-testid="tour-selection">
            <label className="block text-sm font-medium text-[#1C1917] mb-2">
              ¿Qué tour has realizado? *
            </label>
            <div className="space-y-2">
              {tourOptions.map((tour) => (
                <button
                  key={tour.id}
                  type="button"
                  onClick={() => setSelectedTour(tour.id)}
                  data-testid={`tour-option-${tour.id}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                    selectedTour === tour.id
                      ? 'border-[#C2410C] bg-[#C2410C]/5'
                      : 'border-[#E7E5E4] hover:border-[#C2410C]/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${selectedTour === tour.id ? 'text-[#C2410C]' : 'text-[#1C1917]'}`}>
                      {tour.label}
                    </span>
                    <span className="text-sm text-[#78716C]">{tour.price} &euro;</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Star Rating */}
          <div className="text-center">
            <label className="block text-sm font-medium text-[#1C1917] mb-3">
              Tu valoración *
            </label>
            <div className="flex justify-center gap-2" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110 focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`h-12 w-12 md:h-14 md:w-14 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'fill-[#F59E0B] text-[#F59E0B]'
                        : 'text-[#E5E5E5]'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-2 text-[#57534E]"
              >
                {rating === 5 && '¡Excelente!'}
                {rating === 4 && '¡Muy bien!'}
                {rating === 3 && 'Bien'}
                {rating === 2 && 'Regular'}
                {rating === 1 && 'Mejorable'}
              </motion.p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-2">
              Tu nombre *
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              required
              className="text-lg py-6"
              data-testid="review-name-input"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-2">
              País de origen (opcional)
            </label>
            <Input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Estados Unidos, Francia, España..."
              className="text-base"
              data-testid="review-country-input"
            />
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-2">
              Comentario (opcional)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Cuéntanos tu experiencia..."
              rows={3}
              className="text-base"
              data-testid="review-comment-input"
              dir="auto"
            />
            <p className="text-xs text-[#78716C] mt-1">
              Puedes escribir en español, inglés, francés, chino o árabe
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-2">
              Email (opcional)
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="text-base"
              data-testid="review-email-input"
            />
          </div>

          {/* Tip Section */}
          {selectedTour && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="border-t border-[#E7E5E4] pt-6"
              data-testid="tip-section"
            >
              <div className="text-center mb-4">
                <Heart className="h-6 w-6 text-rose-500 mx-auto mb-2" />
                <h3 className="font-semibold text-[#1C1917] text-lg">
                  ¿Quieres dejar una propina a tu guía?
                </h3>
                <p className="text-sm text-[#78716C] mt-1">
                  Si has disfrutado de la experiencia, puedes agradecer el servicio con una propina. Es totalmente opcional.
                </p>
              </div>

              <div className="space-y-2" data-testid="tip-options">
                {TIP_OPTIONS.map((opt) => {
                  const isSelected = tipOption === opt.key;
                  const displayAmount = opt.pct !== null
                    ? `${(tourPrice * opt.pct).toFixed(2)} €`
                    : null;

                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setTipOption(opt.key)}
                      data-testid={`tip-option-${opt.key}`}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-between ${
                        isSelected
                          ? opt.key === 'none'
                            ? 'border-[#78716C] bg-[#F5F3EF]'
                            : 'border-rose-500 bg-rose-50'
                          : 'border-[#E7E5E4] hover:border-rose-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? opt.key === 'none' ? 'border-[#78716C]' : 'border-rose-500'
                            : 'border-[#D6D3D1]'
                        }`}>
                          {isSelected && (
                            <div className={`w-2.5 h-2.5 rounded-full ${
                              opt.key === 'none' ? 'bg-[#78716C]' : 'bg-rose-500'
                            }`} />
                          )}
                        </div>
                        <span className={`font-medium ${isSelected ? 'text-[#1C1917]' : 'text-[#57534E]'}`}>
                          {opt.label}
                        </span>
                      </div>
                      {displayAmount && opt.key !== 'none' && (
                        <span className="text-sm text-[#78716C] font-medium">{displayAmount}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Custom amount input */}
              <AnimatePresence>
                {tipOption === 'custom' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="relative mt-3">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C] font-medium">&euro;</span>
                      <Input
                        type="number"
                        min="1"
                        max="500"
                        step="0.01"
                        value={customTip}
                        onChange={(e) => setCustomTip(e.target.value)}
                        placeholder="Introduce la cantidad"
                        className="pl-8 text-lg py-5"
                        data-testid="tip-custom-input"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tip summary */}
              {tipAmount > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-3 text-center"
                >
                  <p className="text-sm text-rose-600 font-medium" data-testid="tip-summary">
                    Propina: {tipAmount.toFixed(2)} &euro;
                  </p>
                  <p className="text-xs text-[#A8A29E] mt-1">
                    Pago seguro con Stripe
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || !rating || !name.trim() || !selectedTour}
            className="w-full bg-[#C2410C] hover:bg-[#9A3412] rounded-full py-6 text-lg font-semibold transition-all duration-300 hover:scale-[1.02]"
            data-testid="review-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {tipAmount > 0 ? 'Procesando...' : 'Enviando...'}
              </>
            ) : tipAmount > 0 ? (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Enviar valoración y dejar {tipAmount.toFixed(2)} &euro; de propina
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Enviar valoración
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-[#78716C] mt-6">
          Madrid Tuk Tuk Tour &bull; Carlos Casal
        </p>
      </motion.div>
    </div>
  );
};

export default ReviewPage;
